import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, LogOut, Truck, CalendarCheck, Gift, User, ArrowLeft, Trophy,
} from "lucide-react";
import { API } from "../constants/brand";
import { formatEUR } from "../context/CartContext";

const inputCls =
  "w-full rounded-xl bg-[#0B0F17]/80 border border-white/10 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50 transition-colors";

const GoogleButton = ({ label, redirectPath }) => (
  <button
    data-testid="google-signin-button"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + redirectPath;
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="w-full flex items-center justify-center gap-3 rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-slate-200 hover:bg-white/[0.08] hover:border-white/25 transition-colors"
  >
    <svg viewBox="0 0 24 24" className="w-4.5 h-4.5 w-5 h-5" aria-hidden="true">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
    </svg>
    {label}
  </button>
);

const StatusBadge = ({ s }) => {
  const colors = {
    gebucht: "text-sky-300 border-sky-400/30 bg-sky-400/10",
    bestätigt: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
    unterwegs: "text-amber-300 border-amber-400/30 bg-amber-400/10",
    abgeschlossen: "text-slate-300 border-white/15 bg-white/5",
    angefragt: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  };
  return (
    <span className={`text-[10px] font-mono-tech uppercase tracking-widest rounded-full border px-2.5 py-1 ${colors[s] || colors.gebucht}`}>
      {s}
    </span>
  );
};

const Section = ({ icon: Icon, title, testid, empty, children }) => (
  <div data-testid={testid} className="rounded-2xl border border-white/5 bg-white/[0.02] p-5">
    <div className="flex items-center gap-2.5 mb-4">
      <Icon size={16} className="text-cyan-400" />
      <h3 className="text-sm font-semibold text-slate-100">{title}</h3>
    </div>
    {children || <p className="text-xs text-slate-500">{empty}</p>}
  </div>
);

export default function AccountPage() {
  const [state, setState] = useState("loading"); // loading | out | in
  const [customer, setCustomer] = useState(null);
  const [overview, setOverview] = useState(null);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);

  const loadOverview = async () => {
    try {
      const { data } = await axios.get(`${API}/konto/overview`, { withCredentials: true });
      setOverview(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/konto/me`, { withCredentials: true });
        setCustomer(data.customer);
        localStorage.setItem("konto_user", JSON.stringify(data.customer));
        setState("in");
        loadOverview();
      } catch {
        setState("out");
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = mode === "login" ? "/konto/login" : "/konto/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await axios.post(`${API}${url}`, payload, { withCredentials: true });
      setCustomer(data.customer);
      localStorage.setItem("konto_user", JSON.stringify(data.customer));
      setState("in");
      toast.success(`Willkommen, ${data.customer.name}!`);
      loadOverview();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Etwas ist schiefgelaufen.");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/konto/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem("konto_user");
    setCustomer(null);
    setOverview(null);
    setState("out");
    toast.success("Abgemeldet.");
  };

  return (
    <div data-testid="account-page" className="min-h-screen bg-[#0A0E14] text-slate-200">
      <header className="border-b border-white/5">
        <div className="max-w-4xl mx-auto px-5 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="M-Line Solutions" className="w-9 h-9 rounded-lg object-cover" />
            <div>
              <div className="font-semibold text-white text-sm">M-Line Solutions</div>
              <div className="text-[10px] tracking-[0.25em] text-slate-500 font-mono-tech">MEIN KONTO</div>
            </div>
          </div>
          <a data-testid="account-back-link" href="/" className="flex items-center gap-2 text-xs text-slate-400 hover:text-cyan-300 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12">
        {state === "loading" && (
          <div className="flex justify-center py-20"><Loader2 size={32} className="text-cyan-400 animate-spin" /></div>
        )}

        {state === "out" && (
          <div className="max-w-md mx-auto">
            <h1 className="text-3xl font-bold text-white text-center">Dein Konto</h1>
            <p className="text-sm text-slate-400 text-center mt-3 mb-8">
              Transporte, Termine und deine Geschenkkarten — alles an einem Ort. Dein 11. Transport ist kostenlos.
            </p>
            <div className="rounded-2xl border border-white/10 bg-[#111625]/80 p-6 sm:p-8">
              <div className="flex rounded-full border border-white/10 bg-white/[0.03] p-1 mb-6">
                {[["login", "Anmelden"], ["register", "Registrieren"]].map(([id, label]) => (
                  <button
                    key={id}
                    data-testid={`konto-tab-${id}`}
                    onClick={() => setMode(id)}
                    className={`flex-1 rounded-full py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                      mode === id ? "bg-cyan-400/15 text-cyan-300" : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="space-y-3">
                {mode === "register" && (
                  <>
                    <input data-testid="konto-name" required placeholder="Dein Name" value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
                    <input data-testid="konto-phone" placeholder="Telefon (für Treuebonus)" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
                  </>
                )}
                <input data-testid="konto-email" required type="email" placeholder="E-Mail-Adresse" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
                <input data-testid="konto-password" required type="password" minLength={8}
                  placeholder={mode === "register" ? "Passwort (mind. 8 Zeichen)" : "Passwort"} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
                <button data-testid="konto-submit" type="submit" disabled={busy}
                  className="w-full rounded-xl bg-gradient-to-r from-[#1E6FD9] to-cyan-500 py-3 text-sm font-bold text-white hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy && <Loader2 size={15} className="animate-spin" />}
                  {mode === "login" ? "Anmelden" : "Konto erstellen"}
                </button>
              </form>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-widest text-slate-600">oder</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <GoogleButton label="Mit Google anmelden" redirectPath="/konto" />
            </div>
          </div>
        )}

        {state === "in" && customer && (
          <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h1 className="text-3xl font-bold text-white">
                Hallo, <span className="text-gradient-blue">{customer.name}</span>
              </h1>
              <button data-testid="konto-logout" onClick={logout}
                className="flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-xs text-slate-400 hover:text-red-300 hover:border-red-400/30 transition-colors">
                <LogOut size={13} /> Abmelden
              </button>
            </div>

            <div data-testid="loyalty-card" className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-400/10 to-transparent p-5">
              <div className="flex items-center gap-2.5 mb-2">
                <Trophy size={16} className="text-cyan-300" />
                <h3 className="text-sm font-semibold text-slate-100">Treuebonus — dein 11. Transport ist kostenlos</h3>
              </div>
              {overview?.loyalty ? (
                <>
                  <div className="flex gap-1.5 mt-3">
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div key={i} className={`h-2 flex-1 rounded-full ${i < overview.loyalty.rides_in_cycle ? "bg-cyan-400" : "bg-white/10"}`} />
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    {overview.loyalty.free_ride_next
                      ? "Dein nächster Transport ist KOSTENLOS!"
                      : `${overview.loyalty.rides_in_cycle} von 10 Transporten — noch ${10 - overview.loyalty.rides_in_cycle} bis zur Gratisfahrt.`}
                  </p>
                </>
              ) : (
                <p className="text-xs text-slate-500 mt-2">Hinterlege deine Telefonnummer bei der Registrierung, um deinen Bonus zu tracken.</p>
              )}
            </div>

            <Section icon={Truck} title="Meine Transporte" testid="konto-transporte" empty="Noch keine Transporte gebucht.">
              {overview?.rides?.length > 0 && (
                <div className="space-y-2.5">
                  {overview.rides.map((r) => (
                    <div key={r.id} data-testid={`konto-ride-${r.order_no}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                      <div className="min-w-0">
                        <div className="font-mono-tech text-[10px] text-cyan-400/90">{r.order_no}</div>
                        <div className="text-xs text-slate-300 truncate">{r.pickup} → {r.dropoff}</div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono-tech text-xs text-slate-300">{r.is_free ? "GRATIS" : formatEUR(r.price_eur)}</span>
                        <StatusBadge s={r.status} />
                        <a
                          data-testid={`konto-track-${r.order_no}`}
                          href={`/verfolgung?tn=${encodeURIComponent(r.order_no)}&email=${encodeURIComponent(customer?.email || "")}`}
                          className="flex items-center gap-1.5 rounded-full border border-cyan-400/25 bg-cyan-400/10 px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest text-cyan-300 hover:bg-cyan-400/20 transition-colors"
                        >
                          Verfolgen
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={CalendarCheck} title="Meine Werkstatt-Termine" testid="konto-termine" empty="Noch keine Termine gebucht.">
              {overview?.appointments?.length > 0 && (
                <div className="space-y-2.5">
                  {overview.appointments.map((a) => (
                    <div key={a.id} data-testid={`konto-appt-${a.appt_no}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                      <div className="min-w-0">
                        <div className="font-mono-tech text-[10px] text-cyan-400/90">{a.appt_no}</div>
                        <div className="text-xs text-slate-300">{a.service}{a.tire_size ? ` · ${a.tire_size}` : ""}</div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono-tech text-xs text-slate-300">{a.date} · {a.time}</span>
                        <StatusBadge s={a.status} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            <Section icon={Gift} title="Meine Geschenkkarten" testid="konto-gutscheine" empty="Noch keine Geschenkkarten gekauft.">
              {overview?.vouchers?.length > 0 && (
                <div className="space-y-2.5">
                  {overview.vouchers.map((v) => (
                    <div key={v.code} data-testid={`konto-voucher-${v.code}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
                      <div className="min-w-0">
                        <div className="font-mono-tech text-sm font-bold text-cyan-300">{v.code}</div>
                        <div className="text-xs text-slate-400 truncate">{v.product_name}</div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono-tech text-xs text-slate-300">{formatEUR(v.value_eur)}</span>
                        <span className={`text-[10px] font-mono-tech uppercase tracking-widest rounded-full border px-2.5 py-1 ${
                          v.redeemed ? "text-slate-500 border-white/10 bg-white/5" : "text-emerald-300 border-emerald-400/30 bg-emerald-400/10"
                        }`}>
                          {v.redeemed ? "eingelöst" : "gültig"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>
          </div>
        )}
      </main>
    </div>
  );
}

export { GoogleButton };
